import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useI18n } from '../i18n';
import { api } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  ArrowLeft, Search, Users, Bed, Bath, MapPin, X, Filter,
  ChevronRight, Home
} from 'lucide-react';

// Fix Leaflet default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const priceIcon = (price) => L.divIcon({
  className: 'custom-price-marker',
  html: `<div style="
    background: #264639; color: white; padding: 4px 8px; border-radius: 20px;
    font-size: 12px; font-weight: 700; white-space: nowrap; box-shadow: 0 2px 8px rgba(0,0,0,0.3);
    border: 2px solid white; font-family: 'Outfit', sans-serif;
  ">$${price || '?'}</div>`,
  iconSize: [60, 28],
  iconAnchor: [30, 28],
  popupAnchor: [0, -30],
});

const PROPERTY_IMAGES = [
  'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=400',
  'https://images.unsplash.com/photo-1582719508461-905c673771fd?w=400',
  'https://images.unsplash.com/photo-1571896349842-33c89424de2d?w=400',
];

const GUEST_OPTIONS = [1, 2, 3, 4, 5, 6, 8, 10];
const BEDROOM_OPTIONS = [1, 2, 3, 4, 5];

const clusterIconCreator = (cluster) => {
  const count = cluster.getChildCount();
  return L.divIcon({
    html: `<div style="
      background: #264639; color: white; width: 40px; height: 40px;
      border-radius: 50%; display: flex; align-items: center; justify-content: center;
      font-size: 14px; font-weight: 700; box-shadow: 0 3px 10px rgba(0,0,0,0.3);
      border: 3px solid white; font-family: 'Outfit', sans-serif;
    ">${count}</div>`,
    className: 'custom-cluster-icon',
    iconSize: [40, 40],
    iconAnchor: [20, 20],
  });
};

function MapBoundsHandler({ markers }) {
  const map = useMap();
  useEffect(() => {
    if (markers.length > 0) {
      const bounds = L.latLngBounds(markers.map(m => [m.latitude, m.longitude]));
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 14 });
    }
  }, [markers, map]);
  return null;
}

export default function MapPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [markers, setMarkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    search: '', country: '', city: '', guests: '', bedrooms: '', propertyType: ''
  });

  const fetchMarkers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.guests) params.set('guests', filters.guests);
      if (filters.bedrooms) params.set('bedrooms', filters.bedrooms);
      if (filters.propertyType) params.set('property_type', filters.propertyType);
      if (filters.country) params.set('country', filters.country);
      if (filters.city) params.set('city', filters.city);
      const res = await api.get(`/properties/map/markers?${params.toString()}`);
      setMarkers(res.data.markers || []);
    } catch (_) { toast.error('Error al cargar datos'); } finally {
      setLoading(false);
    }
  }, [filters.guests, filters.bedrooms, filters.propertyType, filters.country, filters.city]);

  useEffect(() => { fetchMarkers(); }, [fetchMarkers]);

  const filteredMarkers = useMemo(() => {
    if (!filters.search) return markers;
    const s = filters.search.toLowerCase();
    return markers.filter(m =>
      m.title?.toLowerCase().includes(s) ||
      m.city?.toLowerCase().includes(s) ||
      m.code?.toLowerCase().includes(s)
    );
  }, [markers, filters.search]);

  const countries = useMemo(() => {
    const set = new Set(markers.map(m => m.country).filter(Boolean));
    return Array.from(set).sort();
  }, [markers]);

  const cities = useMemo(() => {
    const set = new Set(markers.map(m => m.city).filter(Boolean));
    return Array.from(set).sort();
  }, [markers]);

  const defaultCenter = [-27.3, -55.9]; // Paraguay / Encarnación area

  return (
    <div className="h-screen flex flex-col" data-testid="map-page">
      {/* Header */}
      <div className="bg-white border-b border-[#E7E5E4] px-4 py-3 flex items-center gap-3 z-[1000] relative">
        <Button
          variant="ghost" size="sm"
          onClick={() => navigate(-1)}
          className="text-[#57534E] hover:text-[#1C1917]"
          data-testid="map-back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-1" /> {t('nav.back') || 'Volver'}
        </Button>

        <div className="flex-1 relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#78716C]" />
          <Input
            value={filters.search}
            onChange={(e) => setFilters(f => ({ ...f, search: e.target.value }))}
            placeholder={t('map.searchPlaceholder') || 'Buscar propiedad, ciudad o código...'}
            className="pl-10 border-[#E7E5E4] h-9 text-sm"
            data-testid="map-search"
          />
        </div>

        <Button
          variant={showFilters ? 'default' : 'outline'}
          size="sm"
          onClick={() => setShowFilters(!showFilters)}
          className={showFilters ? 'bg-[#264639] hover:bg-[#1C352B]' : 'border-[#E7E5E4]'}
          data-testid="map-filter-toggle"
        >
          <Filter className="w-4 h-4 mr-1" /> {t('map.filters') || 'Filtros'}
        </Button>

        <div className="text-sm text-[#78716C]" data-testid="map-count">
          <MapPin className="w-4 h-4 inline mr-1" />
          {filteredMarkers.length} {t('map.properties') || 'propiedades'}
        </div>
      </div>

      {/* Filter Bar */}
      {showFilters && (
        <div className="bg-white border-b border-[#E7E5E4] px-4 py-3 flex flex-wrap gap-3 z-[1000] relative" data-testid="map-filters-bar">
          <Select value={filters.country} onValueChange={(v) => setFilters(f => ({ ...f, country: v === 'all' ? '' : v }))}>
            <SelectTrigger className="w-40 h-9 border-[#E7E5E4] text-sm">
              <SelectValue placeholder={t('map.country') || 'Pais'} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('map.all') || 'Todos'}</SelectItem>
              {countries.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>

          <Select value={filters.city} onValueChange={(v) => setFilters(f => ({ ...f, city: v === 'all' ? '' : v }))}>
            <SelectTrigger className="w-44 h-9 border-[#E7E5E4] text-sm">
              <SelectValue placeholder={t('map.city') || 'Ciudad'} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('map.all') || 'Todas'}</SelectItem>
              {cities.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>

          <Select value={filters.guests} onValueChange={(v) => setFilters(f => ({ ...f, guests: v === 'all' ? '' : v }))}>
            <SelectTrigger className="w-36 h-9 border-[#E7E5E4] text-sm">
              <SelectValue placeholder={t('map.guests') || 'Huéspedes'} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('map.any') || 'Cualquiera'}</SelectItem>
              {GUEST_OPTIONS.map(n => <SelectItem key={n} value={String(n)}>{n}+</SelectItem>)}
            </SelectContent>
          </Select>

          <Select value={filters.bedrooms} onValueChange={(v) => setFilters(f => ({ ...f, bedrooms: v === 'all' ? '' : v }))}>
            <SelectTrigger className="w-36 h-9 border-[#E7E5E4] text-sm">
              <SelectValue placeholder={t('map.bedrooms') || 'Dormitorios'} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('map.any') || 'Cualquiera'}</SelectItem>
              {BEDROOM_OPTIONS.map(n => <SelectItem key={n} value={String(n)}>{n}+</SelectItem>)}
            </SelectContent>
          </Select>

          <Button
            variant="ghost" size="sm"
            onClick={() => setFilters({ search: '', country: '', city: '', guests: '', bedrooms: '', propertyType: '' })}
            className="text-[#78716C] text-sm"
            data-testid="map-clear-filters"
          >
            <X className="w-3 h-3 mr-1" /> {t('map.clearFilters') || 'Limpiar'}
          </Button>
        </div>
      )}

      {/* Map */}
      <div className="flex-1 relative">
        {loading && (
          <div className="absolute inset-0 z-[999] bg-white/70 flex items-center justify-center">
            <div className="animate-spin w-8 h-8 border-3 border-[#264639] border-t-transparent rounded-full" />
          </div>
        )}

        <MapContainer
          center={defaultCenter}
          zoom={6}
          style={{ height: '100%', width: '100%' }}
          zoomControl={true}
          scrollWheelZoom={true}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />

          {filteredMarkers.length > 0 && <MapBoundsHandler markers={filteredMarkers} />}

          <MarkerClusterGroup
            chunkedLoading
            maxClusterRadius={50}
            iconCreateFunction={clusterIconCreator}
          >
            {filteredMarkers.map((marker) => (
              <Marker
                key={marker.property_id}
                position={[marker.latitude, marker.longitude]}
                icon={priceIcon(marker.price)}
                eventHandlers={{
                  click: () => setSelectedProperty(marker),
                }}
              />
            ))}
          </MarkerClusterGroup>
        </MapContainer>

        {/* Property Card Overlay */}
        {selectedProperty && (
          <div
            className="absolute bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:w-96 z-[1000] bg-white rounded-xl shadow-2xl overflow-hidden border border-[#E7E5E4] animate-in slide-in-from-bottom-4"
            data-testid="map-property-card"
          >
            <button
              onClick={() => setSelectedProperty(null)}
              className="absolute top-3 right-3 z-10 w-7 h-7 bg-white/90 rounded-full flex items-center justify-center shadow-sm hover:bg-white transition-colors"
              data-testid="map-card-close"
            >
              <X className="w-4 h-4 text-[#57534E]" />
            </button>

            <div className="flex">
              <img
                src={selectedProperty.image_url || PROPERTY_IMAGES[selectedProperty.property_id?.charCodeAt(0) % 3 || 0]}
                alt={selectedProperty.title}
                className="w-32 h-32 object-cover flex-shrink-0"
              />
              <div className="p-3 flex-1 min-w-0">
                <div className="flex items-center gap-1.5 mb-1">
                  {selectedProperty.property_type && (
                    <span className="text-[10px] bg-[#DCE9E2] text-[#264639] px-1.5 py-0.5 rounded font-medium">
                      {selectedProperty.property_type}
                    </span>
                  )}
                  {selectedProperty.code && (
                    <span className="text-[10px] text-[#78716C]">{selectedProperty.code}</span>
                  )}
                </div>
                <h3 className="font-semibold text-sm text-[#1C1917] line-clamp-2 mb-1" style={{ fontFamily: 'Outfit' }}>
                  {selectedProperty.title}
                </h3>
                <div className="flex items-center text-xs text-[#78716C] mb-2">
                  <MapPin className="w-3 h-3 mr-1" />
                  {selectedProperty.city}{selectedProperty.country ? `, ${selectedProperty.country}` : ''}
                </div>
                <div className="flex items-center gap-3 text-xs text-[#57534E] mb-2">
                  {selectedProperty.max_guests && (
                    <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {selectedProperty.max_guests}</span>
                  )}
                  {selectedProperty.bedrooms && (
                    <span className="flex items-center gap-1"><Bed className="w-3 h-3" /> {selectedProperty.bedrooms}</span>
                  )}
                  {selectedProperty.bathrooms && (
                    <span className="flex items-center gap-1"><Bath className="w-3 h-3" /> {selectedProperty.bathrooms}</span>
                  )}
                </div>
                <div className="flex items-center justify-between">
                  <span className="font-bold text-[#264639]">
                    ${selectedProperty.price} <span className="text-xs font-normal text-[#78716C]">/ {t('detail.night') || 'noche'}</span>
                  </span>
                  <Button
                    size="sm"
                    onClick={() => navigate(`/properties/${selectedProperty.property_id}`)}
                    className="bg-[#264639] hover:bg-[#1C352B] h-7 text-xs px-3"
                    data-testid="map-card-view"
                  >
                    {t('map.viewProperty') || 'Ver'} <ChevronRight className="w-3 h-3 ml-1" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
